import React, { useState, useCallback, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  subscribeToProcessingProgress,
  registerMessageHandler,
  unregisterMessageHandler,
  initWebSocket,
} from "../../services/websocketService";
import "./VideoUpload.css";
import PaymentQuota from "./PaymentQuota";

// ~5MB chunks, which will be ~5.9 MB after base64 encoding
const CHUNK_SIZE = Math.floor(5 * 1024 * 1024);
console.log("NOW UPLOADING WORKING");
const VideoUpload = () => {
  const navigate = useNavigate();
  const [token, setToken] = useState(null);
  const [videoType, setVideoType] = useState("long"); // 'long' or 'reel'
  const [uploadQueue, setUploadQueue] = useState([]);
  const [currentUpload, setCurrentUpload] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [processingProgress, setProcessingProgress] = useState(new Map());
  const [details, setDetails] = useState({
    title: "",
    description: "",
  });
  const [currentUploadId, setCurrentUploadId] = useState(null);
  const uploadControllerRef = useRef(null);
  const fileInputRef = useRef(null);
  const [editingItemIndex, setEditingItemIndex] = useState(null);
  const [processingVideos, setProcessingVideos] = useState(new Map());

  // Get token from localStorage on component mount
  useEffect(() => {
    const storedToken = localStorage.getItem("token");
    if (!storedToken) {
      navigate("/login");
      return;
    }
    initWebSocket(storedToken);
    setToken(storedToken);
  }, [navigate]);

  // Handle processing progress updates from websocket
  useEffect(() => {
    const handleProcessingProgress = (data) => {
      if (!data || (!data.quality && !data.qualities)) return;

      const { videoId, quality, progress, isComplete } = data;
      if (!quality || quality === undefined) return;

      console.log(
        "Processing progress:",
        videoId,
        quality,
        progress,
        isComplete
      );

      if (progress === 108 || isComplete) {
        // Processing complete for this video
        setProcessingVideos((prev) => {
          const newMap = new Map(prev);
          if (newMap.has(videoId)) {
            const videoData = newMap.get(videoId);
            videoData.complete = true;
            newMap.set(videoId, videoData);
          }
          return newMap;
        });

        // Update queue item status
        setUploadQueue((prev) => {
          return prev.map((item) => {
            if (item.uploadId === videoId) {
              return { ...item, status: "complete" };
            }
            return item;
          });
        });

        // If this is the current upload, move to the next one
        if (videoId === currentUploadId) {
          setProcessingProgress(new Map());
          processNextInQueue();
        }
        return;
      }

      // Update processing progress for current upload
      if (videoId === currentUploadId) {
        setProcessingProgress((prevProgress) => {
          const newProgress = new Map(prevProgress);
          newProgress.set(quality, progress);
          return newProgress;
        });
      }

      // Update processing progress in the processing videos map
      setProcessingVideos((prev) => {
        const newMap = new Map(prev);
        if (!newMap.has(videoId)) {
          newMap.set(videoId, {
            qualities: new Map(),
            complete: false,
          });
        }
        const videoData = newMap.get(videoId);
        videoData.qualities.set(quality, progress);
        newMap.set(videoId, videoData);
        return newMap;
      });
    };

    registerMessageHandler("processingProgress", handleProcessingProgress);

    return () => {
      unregisterMessageHandler("processingProgress");
      setProcessingProgress(new Map());
    };
  }, [currentUploadId]);

  // Cleanup function for cancelled uploads
  const cleanupUpload = useCallback(
    async (uploadId) => {
      if (!uploadId) return;
      try {
        await axios.post(
          `/api/videos/cancel-upload`,
          { uploadId },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error("Error cleaning up upload:", error);
      }
    },
    [token]
  );

  // Handle file selection
  const handleFileSelect = useCallback(
    (event) => {
      const files = Array.from(event.target.files);
      if (files.length === 0) return;

      // Add files to queue
      setUploadQueue((prev) => [
        ...prev,
        ...files.map((file) => ({
          file,
          title: "",
          description: "",
          type: videoType,
          status: "pending", // pending, ready, uploading, processing, complete, error
          progress: 0,
          preview: URL.createObjectURL(file),
        })),
      ]);
    },
    [videoType]
  );

  // Process next file in queue
  const processNextInQueue = useCallback(() => {
    setCurrentUpload(null);
    setUploadProgress(0);
    setProcessingProgress(new Map());
    setDetails({ title: "", description: "" });
    setCurrentUploadId(null);

    // Check if there are any ready uploads
    const nextUploadIndex = uploadQueue.findIndex(
      (item) => item.status === "ready"
    );

    if (nextUploadIndex !== -1) {
      // Get the next upload
      const nextUpload = uploadQueue[nextUploadIndex];

      // Start uploading it
      startUpload(nextUpload);
    } else {
      setIsUploading(false);
    }
  }, [uploadQueue]);

  // Upload in chunks
  const uploadInChunks = async (file, uploadId, signal) => {
    try {
      const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
      console.log(`Starting upload of ${file.name} in ${totalChunks} chunks`);

      for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
        const start = chunkNumber * CHUNK_SIZE;
        const end = Math.min(file.size, start + CHUNK_SIZE);
        const chunk = file.slice(start, end);

        // Create FormData
        const formData = new FormData();

        // IMPORTANT: Add these fields BEFORE the file
        formData.append("uploadId", uploadId);
        formData.append("chunkNumber", chunkNumber.toString());
        formData.append("totalChunks", totalChunks.toString());
        formData.append("type", videoType);

        // Add the chunk file last
        formData.append("chunk", chunk, "chunk");

        console.log(
          `Uploading chunk ${chunkNumber + 1}/${totalChunks} for ${file.name}`
        );
        console.log("FormData contents:", {
          uploadId,
          chunkNumber: chunkNumber.toString(),
          totalChunks: totalChunks.toString(),
          type: videoType,
        });

        // Upload the chunk
        await axios.post("/api/videos/upload-chunk", formData, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (progressEvent) => {
            const chunkProgress = progressEvent.loaded / progressEvent.total;
            const overallProgress =
              ((chunkNumber + chunkProgress) / totalChunks) * 100;
            setUploadProgress(overallProgress);

            // Update queue item progress
            setUploadQueue((prev) =>
              prev.map((item) =>
                item.uploadId === uploadId
                  ? { ...item, progress: overallProgress }
                  : item
              )
            );
          },
          signal: signal,
        });
      }

      console.log(`Upload of ${file.name} completed successfully`);
      return true;
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log("Upload cancelled by user");
        throw new Error("Upload cancelled");
      }

      console.error("Error uploading chunks:", error);
      throw error;
    }
  };

  // Start upload process
  const startUpload = useCallback(
    async (uploadItem = null) => {
      const itemToUpload = uploadItem || currentUpload;

      if (!itemToUpload) return;

      if (!itemToUpload.title || !itemToUpload.description) {
        alert(
          "Please fill in both title and description for all videos before uploading"
        );
        return;
      }

      // Update current upload
      setCurrentUpload(itemToUpload);
      setDetails({
        title: itemToUpload.title,
        description: itemToUpload.description,
      });

      // Update status to uploading
      setUploadQueue((prev) => {
        const index = prev.findIndex((item) => item === itemToUpload);
        if (index === -1) return prev;

        const updated = [...prev];
        updated[index] = {
          ...updated[index],
          status: "uploading",
        };
        return updated;
      });

      setIsUploading(true);
      uploadControllerRef.current = new AbortController();

      try {
        // Initialize upload
        const initResponse = await axios.post(
          `/api/videos/initiate-upload`,
          {
            totalChunks: Math.ceil(itemToUpload.file.size / CHUNK_SIZE),
            filename: itemToUpload.file.name,
            title: itemToUpload.title,
            description: itemToUpload.description,
            type: itemToUpload.type || videoType,
            chunkSize: CHUNK_SIZE,
          },
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );

        const { uploadId, status, error } = initResponse.data;
        console.log("responce======", initResponse.data)
        if (status !== 200) {
          if (status === 507) {
            alert('Upload failed: Storage quota exceeded. Please free up space or buy more storage.');
          }
          else {
            alert('Failed to initiate upload', error);
          }
          return;
        }
        setCurrentUploadId(uploadId);

        // Update queue item with uploadId
        setUploadQueue((prev) => {
          const index = prev.findIndex((item) => item === itemToUpload);
          if (index === -1) return prev;

          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            uploadId,
          };
          return updated;
        });

        subscribeToProcessingProgress(uploadId);

        await uploadInChunks(
          itemToUpload.file,
          uploadId,
          uploadControllerRef.current.signal
        );

        // Update status to processing
        setUploadQueue((prev) => {
          const index = prev.findIndex((item) => item === itemToUpload);
          if (index === -1) return prev;

          const updated = [...prev];
          updated[index] = {
            ...updated[index],
            status: "processing",
            progress: 100,
          };
          return updated;
        });
      } catch (error) {
        if (error.name === "AbortError") {
          console.log("Upload was cancelled");
        } else {
          handleUploadError(error);
        }
      }
    },
    [currentUpload, token, videoType]
  );

  // Handle upload error
  const handleUploadError = (error) => {
    console.error("Upload failed:", error);
    alert("There was an error uploading your video. Please try again.");

    if (currentUpload) {
      setUploadQueue((prev) => {
        const index = prev.findIndex((item) => item === currentUpload);
        if (index === -1) return prev;

        const updated = [...prev];
        updated[index] = { ...updated[index], status: "error" };
        return updated;
      });
    }

    setIsUploading(false);

    if (currentUploadId) {
      cleanupUpload(currentUploadId);
    }
  };

  // Cancel current upload
  const cancelUpload = useCallback(() => {
    if (!isUploading || !currentUpload) return;

    if (uploadControllerRef.current) {
      uploadControllerRef.current.abort();
    }

    setUploadQueue((prev) => {
      const index = prev.findIndex((item) => item === currentUpload);
      if (index === -1) return prev;

      const updated = [...prev];
      updated[index] = { ...updated[index], status: "cancelled" };
      return updated;
    });

    if (currentUploadId) {
      cleanupUpload(currentUploadId);
    }

    setIsUploading(false);
    processNextInQueue();
  }, [
    isUploading,
    currentUpload,
    currentUploadId,
    cleanupUpload,
    processNextInQueue,
  ]);

  // Remove item from queue
  const removeFromQueue = useCallback(
    (item) => {
      if (item === currentUpload && isUploading) {
        cancelUpload();
      }

      setUploadQueue((prev) => prev.filter((queueItem) => queueItem !== item));

      // Revoke object URL to prevent memory leaks
      if (item.preview) {
        URL.revokeObjectURL(item.preview);
      }
    },
    [currentUpload, isUploading, cancelUpload]
  );

  // Edit queue item details
  const editQueueItem = (index) => {
    if (isUploading && uploadQueue[index] === currentUpload) {
      alert("Cannot edit details while uploading");
      return;
    }

    setEditingItemIndex(index);
    setDetails({
      title: uploadQueue[index].title || "",
      description: uploadQueue[index].description || "",
    });
  };

  // Save queue item details
  const saveQueueItemDetails = () => {
    if (editingItemIndex === null) return;

    if (!details.title.trim() || !details.description.trim()) {
      alert("Please fill in both title and description");
      return;
    }

    setUploadQueue((prev) => {
      const updated = [...prev];
      updated[editingItemIndex] = {
        ...updated[editingItemIndex],
        title: details.title,
        description: details.description,
        status: "ready", // Mark as ready for upload
      };
      return updated;
    });

    setEditingItemIndex(null);
    setDetails({ title: "", description: "" });
  };

  // Cancel editing
  const cancelEditing = () => {
    setEditingItemIndex(null);
    setDetails({ title: "", description: "" });
  };

  // Render file queue
  const renderFileQueue = () => {
    if (uploadQueue.length === 0) {
      return (
        <div className="empty-queue">
          <p>No files selected for upload</p>
        </div>
      );
    }

    return (
      <div className="file-queue">
        <h3>Upload Queue ({uploadQueue.length} files)</h3>
        <div className="queue-list">
          {uploadQueue.map((item, index) => (
            <div
              key={index}
              className={`queue-item ${item === currentUpload ? "current" : ""
                } ${item.status} ${editingItemIndex === index ? "editing" : ""}`}
              onClick={() => {
                if (!isUploading || item !== currentUpload) {
                  editQueueItem(index);
                }
              }}
            >
              <div className="queue-item-preview">
                <video src={item.preview} />
              </div>
              <div className="queue-item-details">
                <div className="queue-item-name">
                  {item.title
                    ? `${item.title} (${item.file.name})`
                    : item.file.name}
                </div>
                <div className="queue-item-size">
                  {(item.file.size / (1024 * 1024)).toFixed(2)} MB
                </div>
                <div className="queue-item-status">
                  {item.status === "pending" &&
                    "Pending - Click to add details"}
                  {item.status === "ready" && "Ready to upload"}
                  {item.status === "selected" && "Selected - Add details"}
                  {/* {item.status === "uploading" &&
                    `Uploading ${item.progress.toFixed(0)}%`} */}
                  {item.status === "processing" && "Processing"}
                  {item.status === "complete" && "Complete"}
                  {item.status === "error" && "Error"}
                  {item.status === "cancelled" && "Cancelled"}
                </div>
                {item.status === "uploading" || item.status === "processing"}

                {/* Show processing progress bars for processing videos */}
                {item.uploadId &&
                  processingVideos.has(item.uploadId) &&
                  item.status === "processing" && (
                    <div className="mini-processing-progress">
                      {Array.from(
                        processingVideos.get(item.uploadId).qualities.entries()
                      ).map(([quality, progress], i) => (
                        <div
                          key={quality + i}
                          className="mini-quality-progress"
                        >
                          <div className="mini-quality-label">
                            {quality}: {progress}%
                          </div>
                          <div className="mini-progress-bar-background">
                            <div
                              className="mini-progress-bar-fill"
                              style={{
                                width: `${progress || 0}%`,
                                backgroundColor: getProgressColor(progress),
                              }}
                            ></div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
              <button
                className="remove-button"
                onClick={(e) => {
                  e.stopPropagation();
                  removeFromQueue(item);
                }}
                disabled={item.status === "processing" && !item.uploadId}
              >
                {item === currentUpload && isUploading ? "Cancel" : "Remove"}
              </button>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render editing form for queue item
  const renderEditingForm = () => {
    if (editingItemIndex === null) return null;

    const item = uploadQueue[editingItemIndex];

    return (
      <div className="editing-overlay">
        <div className="editing-form">
          <h3>Edit Video Details</h3>
          <div className="preview-container">
            <video src={item.preview} controls className="video-preview" />
          </div>

          <div className="details-form">
            <div className="form-group">
              <label htmlFor="edit-title">Title (required)</label>
              <input
                style={{ color: "#000" }}
                className="title-input"
                type="text"
                id="edit-title"
                value={details.title}
                onChange={(e) =>
                  setDetails((prev) => ({ ...prev, title: e.target.value }))
                }
                placeholder="Add a title that describes your video"
              />
            </div>

            <div className="form-group">
              <label htmlFor="edit-description">Description (required)</label>
              <textarea
                id="edit-description"
                value={details.description}
                onChange={(e) =>
                  setDetails((prev) => ({
                    ...prev,
                    description: e.target.value,
                  }))
                }
                placeholder="Tell viewers about your video"
                rows={4}
              />
            </div>

            <div className="editing-actions">
              <button
                className="save-button"
                onClick={saveQueueItemDetails}
                disabled={!details.title.trim() || !details.description.trim()}
              >
                Save Details
              </button>
              <button className="cancel-button" onClick={cancelEditing}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render processing progress bars
  const renderProcessingProgress = () => {
    if (processingProgress.size === 0) return null;

    return (
      <div className="processing-progress">
        <h3>Processing Progress</h3>
        {Array.from(processingProgress.entries()).map(
          ([quality, progress], i) => (
            <div key={quality + i} className="quality-progress">
              {progress && (
                <>
                  <div className="quality-label-container">
                    <span className="quality-label">{quality}</span>
                    <span className="progress-label">{progress || 0}%</span>
                  </div>
                  <div className="progress-bar-background">
                    <div
                      className="progress-bar-fill"
                      style={{
                        width: `${progress || 0}%`,
                        backgroundColor: getProgressColor(progress),
                      }}
                    ></div>
                  </div>
                </>
              )}
            </div>
          )
        )}
      </div>
    );
  };

  // Helper function to get progress bar color
  const getProgressColor = (progress) => {
    if (progress >= 100) return "#4CAF50";
    if (progress >= 60) return "#2196F3";
    if (progress >= 30) return "#FFA726";
    return "#FF7043";
  };

  // Start queue processing
  const startQueueProcessing = () => {
    if (uploadQueue.length === 0) {
      alert("Please select files to upload");
      return;
    }

    // Check if any files are ready to upload
    const readyFiles = uploadQueue.filter((item) => item.status === "ready");

    if (readyFiles.length === 0) {
      alert(
        "Please add details to at least one video before starting the queue"
      );
      return;
    }

    // Start with the first ready file
    const firstReady = readyFiles[0];
    setCurrentUpload(firstReady);
    startUpload(firstReady);
  };

  return (
    <div className="video-upload-container">
      <div className="upload-header">
        <h2>Upload Videos</h2>
        <div className="video-type-selector">
          <button
            className={`type-button ${videoType === "long" ? "active" : ""}`}
            onClick={() => setVideoType("long")}
          >
            <i className="material-icons">Long Video</i>
            {/* <span>Long Video</span> */}
          </button>
          <button
            className={`type-button ${videoType === "reel" ? "active" : ""}`}
            onClick={() => setVideoType("reel")}
          >
            <i className="material-icons">Short Video</i>
            {/* <span>Reel</span> */}
          </button>
        </div>
      </div>

      {/* Payment & Quota Section */}
      <PaymentQuota token={token} />

      <div className="upload-content">
        <div className="file-selection">
          <input
            type="file"
            accept="video/*"
            multiple
            onChange={handleFileSelect}
            ref={fileInputRef}
            style={{ display: "none" }}
          />
          <button
            className="select-files-button"
            onClick={() => fileInputRef.current.click()}
          >
            <i className="material-icons">cloud_upload</i>
            <span>Select Videos</span>
          </button>
          <p className="file-hint">
            Select videos, add details, then start the upload queue
          </p>
        </div>

        {renderFileQueue()}

        {renderEditingForm()}

        {isUploading && currentUpload && (
          <div className="current-upload-status">
            <h3>
              Currently Uploading:{" "}
              {currentUpload.title || currentUpload.file.name}
            </h3>
            <div className="upload-progress-container">
              <div className="upload-progress-label">
                {uploadProgress === 100
                  ? "Processing..."
                  : `Uploading ${uploadProgress.toFixed(0)}%`}
              </div>
              <div className="progress-bar-background">
                <div
                  className="progress-bar-fill"
                  style={{
                    width: `${uploadProgress}%`,
                    backgroundColor:
                      uploadProgress === 100 ? "#FF9800" : "#2196F3",
                  }}
                ></div>
              </div>
            </div>

            {uploadProgress === 100 && renderProcessingProgress()}

            <button className="cancel-button" onClick={cancelUpload}>
              Cancel Upload
            </button>
          </div>
        )}

        {!isUploading &&
          uploadQueue.filter((item) => item.status === "ready").length > 0 && (
            <div className="queue-actions">
              <button
                className="start-queue-button"
                onClick={startQueueProcessing}
              >
                Start Processing Queue
              </button>
            </div>
          )}

        {/* Processing Videos Summary */}
        {processingVideos.size > 0 && (
          <div className="processing-summary">
            <h3>Processing Videos ({processingVideos.size})</h3>
            <div className="processing-videos-list">
              {Array.from(processingVideos.entries()).map(([videoId, data]) => {
                const queueItem = uploadQueue.find(
                  (item) => item.uploadId === videoId
                );
                if (!queueItem) return null;

                return (
                  <div key={videoId} className="processing-video-item">
                    <div className="processing-video-title">
                      {queueItem.title || queueItem.file.name}
                      {data.complete && (
                        <span className="complete-badge">Complete</span>
                      )}
                    </div>

                    {Array.from(data.qualities.entries()).map(
                      ([quality, progress], i) => (
                        <div key={quality + i} className="quality-progress">
                          <div className="quality-label-container">
                            <span className="quality-label">{quality}</span>
                            <span className="progress-label">
                              {progress || 0}%
                            </span>
                          </div>
                          <div className="progress-bar-background">
                            <div
                              className="progress-bar-fill"
                              style={{
                                width: `${progress || 0}%`,
                                backgroundColor: getProgressColor(progress),
                              }}
                            ></div>
                          </div>
                        </div>
                      )
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default VideoUpload;
